'use client';

import type { CSSProperties } from 'react';
import { PortalLink, tx } from '@akademi/site-builder-runtime';
import { BlockRoot } from '@akademi/site-builder-runtime';
import { DESIGNER_HREF } from '../brand';

type Localized = string | Record<string, string>;

/**
 * `instantQuote*` are retired and ignored.
 *
 * They pointed at `/solar/planner/quick`, a quick-quote funnel whose screen was
 * never mounted and whose public endpoint was never written; it fell through to
 * the project list, so this section offered two buttons to one destination. The
 * props stay in the type because the seeded page passes them — accepting and
 * ignoring them is what keeps a live page from breaking on the next render.
 */
export interface SolarDesignCtaProps {
  eyebrow?: Localized;
  title?: Localized;
  subtitle?: Localized;
  /** @deprecated The quick-quote funnel was retired on 7 October 2026. */
  instantQuoteLabel?: Localized;
  fullDesignLabel?: Localized;
  /** @deprecated The quick-quote funnel was retired on 7 October 2026. */
  instantQuoteHref?: string;
  fullDesignHref?: string;
}

export function SolarDesignCta({
  eyebrow = 'Solar planning, made clear',
  title = 'Design your system on your own roof',
  subtitle = '[confirm] Final system sizing and pricing are checked against your site and catalogue.',
  fullDesignLabel = 'Design your system',
  fullDesignHref = DESIGNER_HREF,
}: SolarDesignCtaProps) {
  return (
    <BlockRoot as="section" style={sectionStyle}>
      <div style={innerStyle}>
        <div style={copyStyle}>
          <span style={eyebrowStyle}>{tx(eyebrow)}</span>
          <h2 style={titleStyle}>{tx(title)}</h2>
          <p style={bodyStyle}>{tx(subtitle)}</p>
        </div>
        <div style={cardsStyle}>
          <CtaCard href={fullDesignHref} label={fullDesignLabel} tone="brand" />
        </div>
      </div>
    </BlockRoot>
  );
}

function CtaCard({ href, label, tone }: { href: string; label: Localized; tone: 'brand' | 'plain' }) {
  return (
    <PortalLink href={href} style={{ ...cardStyle, ...(tone === 'brand' ? brandCardStyle : plainCardStyle) }}>
      <span>{tx(label)}</span><span aria-hidden>→</span>
    </PortalLink>
  );
}

const sectionStyle: CSSProperties = { padding: 'clamp(40px, 7vw, 88px) var(--space-6, 24px)' };
const innerStyle: CSSProperties = { maxWidth: 1180, margin: '0 auto' };
const copyStyle: CSSProperties = { maxWidth: 680, marginBottom: 28 };
const eyebrowStyle: CSSProperties = { color: 'var(--color-brand-700, #1d4ed8)', fontSize: 'var(--font-size-sm, 14px)', fontWeight: 700, letterSpacing: '.08em', textTransform: 'uppercase' };
const titleStyle: CSSProperties = { margin: '10px 0 12px', fontSize: 'clamp(22px, 2.2vw, 28px)', lineHeight: 1.08, fontFamily: 'var(--font-family-display, inherit)' };
const bodyStyle: CSSProperties = { margin: 0, color: 'var(--color-text-secondary, #475569)', lineHeight: 1.6 };
const cardsStyle: CSSProperties = { display: 'grid', gridTemplateColumns: 'minmax(220px, 420px)', gap: 16 };
const cardStyle: CSSProperties = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16, padding: '22px 24px', borderRadius: 'var(--radius-lg, 12px)', textDecoration: 'none', fontWeight: 700, boxShadow: 'var(--shadow-md, 0 6px 18px rgba(15, 23, 42, .12))' };
const brandCardStyle: CSSProperties = { background: 'var(--color-brand-600, #1d4ed8)', color: 'var(--color-text-on-brand, #fff)' };
const plainCardStyle: CSSProperties = { background: 'var(--color-surface-page, #fff)', color: 'var(--color-text-primary, #0f172a)', border: '1px solid var(--color-border-default, #cbd5e1)' };
